import { useState } from 'react';
import type { FormEvent } from 'react';

const FORM_URL = 'https://readdy.ai/api/form/d9umlv2io0qo9emssgs0';

const GOOGLE_SHEET_URL =
  'https://script.google.com/macros/s/AKfycbxevGLZ_8IOXuOFiXUKWzMO6Ec37EUBNCyl9JqifbKhdxpCtrK9nLnIhGP5rWmmu6Y-/exec';

type FormStatus = 'idle' | 'submitting' | 'success' | 'error';

const inputClass =
  'w-full px-4 py-3 bg-background-50/60 border border-background-300/60 rounded-md text-sm text-foreground-100 placeholder:text-foreground-600 focus:outline-none focus:border-primary-500 focus:ring-1 focus:ring-primary-500/30 transition-colors';

export default function InterestSection() {
  const [status, setStatus] = useState<FormStatus>('idle');
  const [errorMessage, setErrorMessage] = useState('');

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);

    const honeypot = String(formData.get('website_alt') ?? '').trim();
    if (honeypot) {
      setStatus('success');
      form.reset();
      return;
    }

    formData.delete('website_alt');

    setStatus('submitting');
    setErrorMessage('');

    try {
      const params = new URLSearchParams();
      formData.forEach((value, key) => {
        if (typeof value === 'string') {
          params.append(key, value);
        }
      });

      // 구글 스프레드시트 연동 (등록 성공 여부와 무관하게 별도로 전송)
      fetch(GOOGLE_SHEET_URL, {
        method: 'POST',
        mode: 'no-cors',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8' },
        body: params.toString(),
      }).catch(() => {
        /* 스프레드시트 전송 실패는 사용자에게 노출하지 않음 */
      });

      const response = await fetch(FORM_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8' },
        body: params.toString(),
      });

      const responseText = await response.text();
      let parsed: {
        code?: string;
        message?: string;
        meta?: { message?: string; detail?: string };
      } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      const code = parsed?.code;
      const serverMessage = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || '';

      if (response.ok && code === 'OK') {
        setStatus('success');
        form.reset();
      } else {
        setStatus('error');
        setErrorMessage(serverMessage || '등록 중 문제가 발생했습니다. 다시 시도해주세요.');
      }
    } catch {
      setStatus('error');
      setErrorMessage('네트워크 오류가 발생했습니다. 다시 시도해주세요.');
    }
  };

  return (
    <section id="register" className="relative w-full bg-background-50 overflow-hidden">
      <div className="relative section-padding py-20 md:py-28">
        <div className="max-w-2xl mx-auto">
          {/* Title */}
          <div className="text-center mb-10 md:mb-14">
            <p className="text-xs md:text-sm font-medium text-primary-500 tracking-[0.3em] mb-2">
              REGISTER
            </p>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-heading font-bold text-foreground-50">
              관심고객 등록
            </h2>
            <p className="text-sm md:text-base text-foreground-400 mt-4">
              상담을 원하시면 아래 정보를 남겨주세요. 빠르게 연락드리겠습니다.
            </p>
          </div>

          {/* Form */}
          <form
            id="interest-register-form"
            data-readdy-form="true"
            onSubmit={handleSubmit}
            className="p-6 md:p-8 bg-background-100/50 border border-background-200/30 rounded-xl"
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div>
                <label
                  htmlFor="interest-name"
                  className="block text-xs md:text-sm font-medium text-foreground-300 mb-2"
                >
                  이름 <span className="text-primary-500">*</span>
                </label>
                <input
                  id="interest-name"
                  name="name"
                  type="text"
                  required
                  placeholder="성함을 입력해주세요"
                  className={inputClass}
                />
              </div>
              <div>
                <label
                  htmlFor="interest-phone"
                  className="block text-xs md:text-sm font-medium text-foreground-300 mb-2"
                >
                  연락처 <span className="text-primary-500">*</span>
                </label>
                <input
                  id="interest-phone"
                  name="phone"
                  type="tel"
                  required
                  placeholder="010-0000-0000"
                  className={inputClass}
                />
              </div>
            </div>

            <div className="mt-5">
              <label
                htmlFor="interest-visit-date"
                className="block text-xs md:text-sm font-medium text-foreground-300 mb-2"
              >
                방문일자
              </label>
              <input
                id="interest-visit-date"
                name="visit_date"
                type="date"
                className={`${inputClass} [color-scheme:dark]`}
              />
            </div>

            <div className="mt-5">
              <label
                htmlFor="interest-message"
                className="block text-xs md:text-sm font-medium text-foreground-300 mb-2"
              >
                문의내용
              </label>
              <textarea
                id="interest-message"
                name="message"
                rows={4}
                maxLength={500}
                placeholder="문의하실 내용을 입력해주세요 (500자 이내)"
                className={`${inputClass} resize-none`}
              />
            </div>

            {/* Spam protection field (kept off-screen via global CSS) */}
            <input
              name="website_alt"
              type="text"
              tabIndex={-1}
              autoComplete="off"
              aria-hidden="true"
              readOnly
              className="form-aux-field"
            />

            <div className="mt-7">
              <button
                type="submit"
                disabled={status === 'submitting'}
                className="w-full inline-flex items-center justify-center gap-2 px-8 py-4 bg-primary-500 hover:bg-primary-600 disabled:opacity-60 disabled:cursor-not-allowed text-background-50 font-semibold rounded-full transition-colors duration-300 cursor-pointer whitespace-nowrap"
              >
                {status === 'submitting' ? '등록 중...' : '등록하기'}
              </button>
            </div>

            {status === 'success' && (
              <p className="mt-4 text-sm text-primary-400 text-center">
                등록이 완료되었습니다. 빠른 시일 내에 연락드리겠습니다.
              </p>
            )}
            {status === 'error' && (
              <p className="mt-4 text-sm text-accent-400 text-center" role="alert">
                {errorMessage}
              </p>
            )}

            <p className="mt-4 text-[11px] text-foreground-600 text-center">
              ※ 등록해주신 정보는 상담 목적으로만 사용됩니다.
            </p>
          </form>
        </div>
      </div>
    </section>
  );
}